import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, ClickAwayListener, Divider, Grow, MenuItem, MenuList, Popper, Stack, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useDispatch } from 'react-redux';
import { authActions } from 'store/auth/authSlice';
import { StyledAvatar, StyledPaper, StyledBox, IconWrapper } from './UserListDropDownStyled';
import PropTypes from 'prop-types';
import PersonIcon from '@mui/icons-material/Person';
import { SSO_REDIRECT_TO_ADDRESS } from 'config';
import { openInNewTab } from 'utils/domHelper';

const UserListDropDown = ({ user }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const handleToggle = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    const logoutHandler = useCallback(() => {
        dispatch(authActions.logoutStart());
    }, [dispatch]);

    const changePwdHandler = useCallback(() => {
        openInNewTab(`${SSO_REDIRECT_TO_ADDRESS}/password-change`);
    }, []);

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setOpen(false);
    };

    const handleListKeyDown = (event) => {
        if (event.key === 'Tab' || event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
        }
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current && !open) {
            anchorRef.current?.focus();
        }
        prevOpen.current = open;
    }, [open]);

    return (
        <Stack direction="row">
            <Button
                ref={anchorRef}
                id="composition-button"
                aria-controls={open ? 'composition-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                style={{ padding: 0, minWidth: 0 }}
            >
                <StyledAvatar>{user?.username?.charAt(0).toUpperCase() || 'U'}</StyledAvatar>
            </Button>

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
                style={{ zIndex: 1200 }}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
                        <StyledPaper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList
                                    autoFocusItem={open}
                                    id="composition-menu"
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                >
                                    <StyledBox paddingTop={0.5} paddingBottom={1} paddingLeft={2} paddingRight={2}>
                                        <Typography variant="body1" fontWeight="500">
                                            {user?.username || 'User'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {user?.email}
                                        </Typography>
                                    </StyledBox>
                                    <Divider />
                                    <MenuItem onClick={changePwdHandler}>
                                        <IconWrapper>
                                            <PersonIcon />
                                        </IconWrapper>
                                        Change password
                                    </MenuItem>
                                    <MenuItem onClick={logoutHandler}>
                                        <IconWrapper>
                                            <LogoutIcon />
                                        </IconWrapper>
                                        Log out
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </StyledPaper>
                    </Grow>
                )}
            </Popper>
        </Stack>
    );
};

export default UserListDropDown;

UserListDropDown.propTypes = {
    user: PropTypes.shape({
        username: PropTypes.string,
        email: PropTypes.string,
    }),
};
